// MangaRelated — Full implementation of Related Manga tab
// Mirrors the anime version (NewRelatedAnime + RelatedAnime + RemovedAnimes) exactly.

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useMangaSource } from '../context/MangaSourceContext';
import { useAniList } from '../context/AniListContext';
import { useBatchOperation } from '../context/BatchOperationContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useAlert } from '../context/ToastContext';
import { EXCLUDED_DISPLAY_RELATION_TYPES } from '../config/constants';
import { getRelationTypeDisplay, formatReleaseDate, getDisplayTitle } from '../services/groupingService';
import { groupPendingAnimeByRelation, sortPendingGroups } from '../services/discoverGroupingService';
import { addAnimeWithRetry } from '../services/aniListService';
import { getRemovedMangaIds, addRemovedManga, removeRemovedManga, initRemovedMangaIds } from '../services/removedMangaService';
import ConfirmationModal from './ConfirmationModal';
import { RelatedSkeleton } from './common/SkeletonLoader';
import './RelatedAnime.css';
import './NewRelatedAnime.css';
import './NewRelatedAnimeGrouped.css';
import './Search.css'; // Reuse modal styles

const MangaRelated = () => {
    const { mangaUserData, isLoading, pendingManga, mangaGroups, pendingChapterMap } = useMangaSource();
    const { token } = useAniList();
    const batchOp = useBatchOperation();
    const { showError, showWarning, showInfo } = useAlert();
    const { titlePreference } = useTitlePreference();

    // Tab state
    const [activeTab, setActiveTab] = useState('new');

    // Separate grouped/flat view state for each sub-tab
    const [newShowGrouped, setNewShowGrouped] = useState(true);
    const [removedShowGrouped, setRemovedShowGrouped] = useState(true);

    // Selection state
    const [selectedManga, setSelectedManga] = useState(new Set());
    const [addingManga, setAddingManga] = useState(new Set());
    const [removedIdsVersion, setRemovedIdsVersion] = useState(0);
    const [addStatus, setAddStatus] = useState('PLANNING');
    const [filterRelationType, setFilterRelationType] = useState('ALL');
    const [successMessage, setSuccessMessage] = useState('');
    const [showRemoveAllModal, setShowRemoveAllModal] = useState(false);

    // Detail modal state
    const [detailManga, setDetailManga] = useState(null);

    // Grouped view state
    const [expandedGroups, setExpandedGroups] = useState(new Set());

    // Initialize removed manga IDs on mount
    useEffect(() => {
        initRemovedMangaIds().then(() => {
            setRemovedIdsVersion(c => c + 1);
        });
    }, []);

    // Listen for removedMangaChanged events
    useEffect(() => {
        const handler = () => setRemovedIdsVersion(prev => prev + 1);
        window.addEventListener('removedMangaChanged', handler);
        return () => window.removeEventListener('removedMangaChanged', handler);
    }, []);

    // Prevent body scroll when detail modal is open
    useEffect(() => {
        if (detailManga) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [detailManga]);

    // Get title using preference
    const getTitle = useCallback((manga) => {
        const t = manga.title;
        if (!t) return `Manga #${manga.id}`;
        if (titlePreference === 'romanji') {
            return t.romaji || t.english || t.native || `Manga #${manga.id}`;
        }
        return t.english || t.romaji || t.native || `Manga #${manga.id}`;
    }, [titlePreference]);

    // Helper to format manga info for modal
    const formatMangaInfo = (manga) => {
        const STATUS_MAP = { 'FINISHED': 'Finished', 'RELEASING': 'Publishing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' };
        const parts = [];
        if (manga.format) parts.push(manga.format);
        if (manga.chapters) parts.push(`${manga.chapters} ch`);
        if (manga.volumes) parts.push(`${manga.volumes} vol`);
        if (manga.status) parts.push(STATUS_MAP[manga.status] || manga.status);
        return parts.join(' • ');
    };

    // Excluded relation types — uses shared constant from constants.js (single source of truth)
    const UI_EXCLUDED_RELATION_TYPES = EXCLUDED_DISPLAY_RELATION_TYPES;

    // Compute filtered new manga
    const newRelatedManga = useMemo(() => {
        const removedIds = getRemovedMangaIds();
        let filtered = (pendingManga || [])
            .filter(m => !removedIds.has(m.id))
            .filter(m => !UI_EXCLUDED_RELATION_TYPES.has(m.relationType));
        if (filterRelationType !== 'ALL') {
            filtered = filtered.filter(m => m.relationType === filterRelationType);
        }
        return filtered;
    }, [pendingManga, removedIdsVersion, filterRelationType]);

    // Compute removed manga
    const removedMangaList = useMemo(() => {
        const removedIds = getRemovedMangaIds();
        return (pendingManga || []).filter(m => removedIds.has(m.id));
    }, [pendingManga, removedIdsVersion]);

    // Get unique relation types for filter (excluding hidden types)
    const relationTypes = useMemo(() => {
        const removedIds = getRemovedMangaIds();
        const nonRemoved = (pendingManga || [])
            .filter(m => !removedIds.has(m.id))
            .filter(m => !UI_EXCLUDED_RELATION_TYPES.has(m.relationType));
        const types = new Set(nonRemoved.map(m => m.relationType));
        return ['ALL', ...Array.from(types)];
    }, [pendingManga, removedIdsVersion]);

    // Grouped view data for new manga tab
    const pendingGroups = useMemo(() => {
        if (!newShowGrouped) return [];
        const removedIds = getRemovedMangaIds();
        const nonRemoved = (pendingManga || [])
            .filter(m => !removedIds.has(m.id))
            .filter(m => !UI_EXCLUDED_RELATION_TYPES.has(m.relationType));
        let groups = groupPendingAnimeByRelation(nonRemoved, mangaGroups || []);

        if (filterRelationType !== 'ALL') {
            groups = groups.map(group => ({
                ...group,
                pendingAnime: group.pendingAnime.filter(m => m.relationType === filterRelationType),
                stats: { ...group.stats, count: group.pendingAnime.filter(m => m.relationType === filterRelationType).length }
            })).filter(group => group.pendingAnime.length > 0);
        }

        return sortPendingGroups(groups, 'name', 'asc');
    }, [pendingManga, mangaGroups, newShowGrouped, filterRelationType, removedIdsVersion]);

    // Grouped view data for removed manga tab
    const removedGroups = useMemo(() => {
        if (!removedShowGrouped) return [];
        return sortPendingGroups(
            groupPendingAnimeByRelation(removedMangaList, mangaGroups || []),
            'name', 'asc'
        );
    }, [removedMangaList, mangaGroups, removedShowGrouped]);

    // --- Actions ---

    const handleAddManga = async (mangaId) => {
        if (!token) {
            showError('OAuth token required to add manga. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }
        setAddingManga(prev => new Set(prev).add(mangaId));
        try {
            await addAnimeWithRetry(mangaId, addStatus, token);
            await addRemovedManga(mangaId);
            const manga = (pendingManga || []).find(m => m.id === mangaId);
            setSuccessMessage(`✅ Added "${manga ? getTitle(manga) : 'Manga'}" as ${addStatus}`);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            showError(`Failed to add manga: ${error.message}`, 'Add Failed');
        } finally {
            setAddingManga(prev => { const n = new Set(prev); n.delete(mangaId); return n; });
        }
    };

    const handleRemoveManga = async (mangaId) => {
        const success = await addRemovedManga(mangaId);
        if (success) {
            flushSync(() => {
                setRemovedIdsVersion(prev => prev + 1);
            });
            setSuccessMessage('Manga removed! ✅');
            setTimeout(() => setSuccessMessage(''), 3000);
        } else {
            showError('Failed to remove manga', 'Remove Failed');
        }
    };

    const handleRestoreManga = async (mangaId) => {
        const success = await removeRemovedManga(mangaId);
        if (success) {
            setSelectedManga(prev => {
                const next = new Set(prev);
                next.delete(mangaId);
                return next;
            });
            flushSync(() => {
                setRemovedIdsVersion(prev => prev + 1);
            });
        } else {
            showError('Failed to restore manga', 'Restore Failed');
        }
    };

    const handleAddSelected = async () => {
        if (selectedManga.size === 0) {
            showWarning('No manga selected', 'No Selection');
            return;
        }
        if (!token) {
            showError('OAuth token required to add manga. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        const totalCount = selectedManga.size;
        let successCount = 0;
        let failCount = 0;

        // Start batch operation tracking
        batchOp.startOperation('adding', totalCount);

        for (const id of selectedManga) {
            if (batchOp.checkCancelled()) {
                console.log('🛑 Batch operation cancelled by user');
                break;
            }
            const manga = (pendingManga || []).find(m => m.id === id);
            batchOp.updateCurrentItem({
                id,
                title: manga?.title?.romaji || manga?.title?.english || `ID: ${id}`,
                attempt: 1
            });
            try {
                await addAnimeWithRetry(id, addStatus, token);
                await addRemovedManga(id);
                successCount++;
                batchOp.markItemCompleted();
            } catch (error) {
                console.error(`Failed to add manga ${id}:`, error);
                failCount++;
                batchOp.markItemFailed();
            }
        }

        setSelectedManga(new Set());

        const wasCancelled = batchOp.isCancelled;
        if (wasCancelled) {
            setSuccessMessage(`⚠️ Cancelled: Added ${successCount}/${totalCount} selected manga.`);
        } else if (failCount === 0) {
            setSuccessMessage(`✅ Successfully added all ${totalCount} manga!`);
        } else {
            setSuccessMessage(`⚠️ Added ${successCount}/${totalCount} manga. ${failCount} failed.`);
        }

        batchOp.endOperation();
        setTimeout(() => setSuccessMessage(''), 5000);
    };

    const handleRemoveSelected = async () => {
        if (selectedManga.size === 0) {
            showWarning('No manga selected', 'No Selection');
            return;
        }
        let successCount = 0;
        for (const id of selectedManga) {
            const success = await addRemovedManga(id);
            if (success) successCount++;
        }
        if (successCount > 0) {
            setSuccessMessage(`✅ Removed ${successCount} selected manga!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setSelectedManga(new Set());
            flushSync(() => {
                setRemovedIdsVersion(prev => prev + 1);
            });
        }
    };

    const handleRestoreSelected = async () => {
        if (selectedManga.size === 0) return;
        for (const id of selectedManga) { await removeRemovedManga(id); }
        setSelectedManga(new Set());
    };

    // Selection helpers
    const toggleSelect = (id) => {
        setSelectedManga(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const selectAll = () => {
        const list = activeTab === 'new' ? newRelatedManga : removedMangaList;
        setSelectedManga(new Set(list.map(m => m.id)));
    };

    const deselectAll = () => setSelectedManga(new Set());

    // Group helpers
    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
            return next;
        });
    };

    const selectAllInGroup = (group) => {
        setSelectedManga(prev => {
            const next = new Set(prev);
            group.pendingAnime.forEach(m => next.add(m.id));
            return next;
        });
    };

    const deselectAllInGroup = (group) => {
        setSelectedManga(prev => {
            const next = new Set(prev);
            group.pendingAnime.forEach(m => next.delete(m.id));
            return next;
        });
    };

    const addAllInGroup = async (group) => {
        if (!token) {
            showError('OAuth token required to add manga.', 'Authentication Required');
            return;
        }
        if (group.pendingAnime.length === 0) return;

        const totalCount = group.pendingAnime.length;
        let successCount = 0;
        let failCount = 0;

        batchOp.startOperation('adding', totalCount);

        for (const manga of group.pendingAnime) {
            if (batchOp.checkCancelled()) break;
            batchOp.updateCurrentItem({ id: manga.id, title: manga.title?.romaji || manga.title?.english, attempt: 1 });
            try {
                await addAnimeWithRetry(manga.id, addStatus, token);
                await addRemovedManga(manga.id);
                successCount++;
                batchOp.markItemCompleted();
            } catch (error) {
                failCount++;
                batchOp.markItemFailed();
            }
        }

        const wasCancelled = batchOp.isCancelled;
        if (wasCancelled) {
            setSuccessMessage(`⚠️ Cancelled: Added ${successCount}/${totalCount} manga from ${group.name}.`);
        } else if (failCount === 0) {
            setSuccessMessage(`✅ Successfully added all ${totalCount} manga from ${group.name}!`);
        } else {
            setSuccessMessage(`⚠️ Added ${successCount}/${totalCount} manga from ${group.name}. ${failCount} failed.`);
        }

        batchOp.endOperation();
        setTimeout(() => setSuccessMessage(''), 5000);
    };

    const removeAllInGroup = async (group) => {
        if (group.pendingAnime.length === 0) return;
        let successCount = 0;
        for (const manga of group.pendingAnime) {
            const success = await addRemovedManga(manga.id);
            if (success) successCount++;
        }
        if (successCount > 0) {
            setSuccessMessage(`✅ Removed ${successCount} manga from ${group.name}!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            flushSync(() => {
                setRemovedIdsVersion(prev => prev + 1);
            });
        }
    };

    const removeAllPending = async () => {
        const mangaToRemove = newShowGrouped
            ? pendingGroups.flatMap(g => g.pendingAnime)
            : newRelatedManga;
        if (mangaToRemove.length === 0) return;
        setShowRemoveAllModal(true);
    };

    const confirmRemoveAllPending = async () => {
        setShowRemoveAllModal(false);
        const mangaToRemove = newShowGrouped
            ? pendingGroups.flatMap(g => g.pendingAnime)
            : newRelatedManga;

        let successCount = 0;
        for (const manga of mangaToRemove) {
            const success = await addRemovedManga(manga.id);
            if (success) successCount++;
        }
        if (successCount > 0) {
            setSuccessMessage(`✅ Removed ${successCount} manga!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setSelectedManga(new Set());
            flushSync(() => {
                setRemovedIdsVersion(prev => prev + 1);
            });
        }
    };

    const restoreAllInGroup = async (group) => {
        const restoredIds = new Set();
        for (const manga of group.pendingAnime) {
            const success = await removeRemovedManga(manga.id);
            if (success) restoredIds.add(manga.id);
        }
        if (restoredIds.size > 0) {
            setSuccessMessage(`✅ Restored ${restoredIds.size} manga from ${group.name}!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setSelectedManga(prev => {
                const next = new Set(prev);
                restoredIds.forEach(id => next.delete(id));
                return next;
            });
            flushSync(() => {
                setRemovedIdsVersion(prev => prev + 1);
            });
        }
    };

    if (isLoading && !mangaUserData) {
        return <RelatedSkeleton />;
    }

    // --- Render card (same structure as anime) ---
    const renderMangaCard = (manga, actions, onClick) => {
        const title = getTitle(manga);
        const coverImage = manga.coverImage?.extraLarge || manga.coverImage?.large || manga.coverImage?.medium;
        const isSelected = selectedManga.has(manga.id);
        const isAdding = addingManga.has(manga.id);
        const releaseDate = formatReleaseDate(manga.startDate);

        // Use pendingChapterMap fallback for chapters if AniList doesn't have the data
        const muData = pendingChapterMap?.[String(manga.id)];
        const displayChapters = manga.chapters || (muData?.latestChapter ? muData.latestChapter : null);

        return (
            <div
                key={manga.id}
                className={`pending-card ${isSelected ? 'selected' : ''}`}
                onClick={() => onClick ? onClick() : setDetailManga(manga)}
                style={{ cursor: 'pointer' }}
            >
                {/* Selection Checkbox */}
                <div className="selection-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(manga.id)}
                    />
                </div>

                {/* Cover Image */}
                <div className="anime-cover-container">
                    {coverImage ? (
                        <img src={coverImage} alt={title} className="anime-cover" />
                    ) : (
                        <div className="anime-cover" style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '2rem', background: 'rgba(255,255,255,0.05)'
                        }}>📚</div>
                    )}
                </div>

                {/* Manga Info */}
                <div className="anime-info">
                    <h3 className="anime-title">
                        <a
                            href={`https://anilist.co/manga/${manga.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {title}
                        </a>
                    </h3>

                    {/* Relation Info */}
                    <div className="relation-info">
                        <span className="relation-badge">
                            {getRelationTypeDisplay(manga.relationType)}
                        </span>
                        {manga.relatedTo && (
                            <span className="related-to">
                                → {manga.relatedTo.title}
                            </span>
                        )}
                    </div>

                    {/* Meta */}
                    <div className="anime-meta">
                        <span>📅 {releaseDate}</span>
                        {displayChapters && (
                            <>
                                <span>•</span>
                                <span>📖 {displayChapters} ch</span>
                            </>
                        )}
                        {manga.volumes && (
                            <>
                                <span>•</span>
                                <span>📚 {manga.volumes} vol</span>
                            </>
                        )}
                        {manga.averageScore && (
                            <>
                                <span>•</span>
                                <span>⭐ {(manga.averageScore / 10).toFixed(1)}</span>
                            </>
                        )}
                        {manga.format && (
                            <>
                                <span>•</span>
                                <span>{manga.format}</span>
                            </>
                        )}
                        {manga.status && (
                            <>
                                <span>•</span>
                                <span>{({ 'FINISHED': 'Finished', 'RELEASING': 'Publishing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' })[manga.status] || manga.status}</span>
                            </>
                        )}
                    </div>

                    {/* Description (truncated) */}
                    {manga.description && (
                        <p className="anime-description">
                            {manga.description.replace(/<[^>]*>/g, '').substring(0, 150)}...
                        </p>
                    )}

                    {/* Quick Actions */}
                    <div className="anime-actions" onClick={(e) => e.stopPropagation()}>
                        {actions === 'add' && (
                            <>
                                <button
                                    onClick={() => handleAddManga(manga.id)}
                                    disabled={isAdding}
                                    className="add-btn"
                                >
                                    {isAdding ? '⏳ Adding...' : '➕ Add to List'}
                                </button>
                                <button
                                    onClick={() => handleRemoveManga(manga.id)}
                                    className="remove-btn"
                                >
                                    🗑️ Remove
                                </button>
                            </>
                        )}
                        {actions === 'restore' && (
                            <button
                                onClick={() => handleRestoreManga(manga.id)}
                                className="add-btn"
                            >
                                ♻️ Restore
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // --- Render grouped view (same as anime) ---
    const renderGroupedView = (groups, actions) => (
        <div className="pending-groups-container">
            {groups.length === 0 ? (
                <div className="empty-state"><p>No manga found matching your filters.</p></div>
            ) : (
                groups.map(group => {
                    const isExpanded = expandedGroups.has(group.id);
                    const groupSelectedCount = group.pendingAnime.filter(m => selectedManga.has(m.id)).length;

                    return (
                        <div key={group.id} className={`pending-group-card ${isExpanded ? 'expanded' : ''}`}>
                            {/* Group Header */}
                            <div className="pending-group-header">
                                <div className="group-selection-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={group.pendingAnime.every(m => selectedManga.has(m.id))}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            if (group.pendingAnime.every(m => selectedManga.has(m.id))) {
                                                deselectAllInGroup(group);
                                            } else {
                                                selectAllInGroup(group);
                                            }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                <div className="group-header-main" onClick={() => toggleGroup(group.id)}>
                                    <div className="group-title-section">
                                        <h2 className="group-name">
                                            {isExpanded ? '📖' : '📚'} {group.name}
                                        </h2>
                                        <div className="group-stats-mini">
                                            <span className="stat-count">{group.stats.count} manga</span>
                                            {group.stats.averageScore > 0 && (
                                                <>
                                                    <span>•</span>
                                                    <span className="stat-score">⭐ {(group.stats.averageScore / 10).toFixed(1)}</span>
                                                </>
                                            )}
                                            {groupSelectedCount > 0 && (
                                                <>
                                                    <span>•</span>
                                                    <span className="stat-selected">✅ {groupSelectedCount} selected</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <button className="expand-btn" title={isExpanded ? 'Collapse' : 'Expand'}>
                                        {isExpanded ? '▼' : '▶'}
                                    </button>
                                </div>

                                {/* Group Actions */}
                                <div className="group-actions">
                                    <button
                                        onClick={() => groupSelectedCount === group.stats.count ? deselectAllInGroup(group) : selectAllInGroup(group)}
                                        className="group-action-btn"
                                    >
                                        {groupSelectedCount === group.stats.count ? '❌ Deselect All' : '✅ Select All'}
                                    </button>
                                    {actions === 'add' && (
                                        <>
                                            <button
                                                onClick={() => addAllInGroup(group)}
                                                disabled={addingManga.size > 0}
                                                className="group-action-btn add-group-btn"
                                            >
                                                {addingManga.size > 0 ? '⏳ Adding...' : `➕ Add All (${group.stats.count})`}
                                            </button>
                                            <button
                                                onClick={() => removeAllInGroup(group)}
                                                className="group-action-btn remove-group-btn"
                                            >
                                                🗑️ Remove All ({group.stats.count})
                                            </button>
                                        </>
                                    )}
                                    {actions === 'restore' && (
                                        <button
                                            onClick={() => restoreAllInGroup(group)}
                                            className="group-action-btn add-group-btn"
                                        >
                                            ♻️ Restore All ({group.stats.count})
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Group Content (Expanded) */}
                            {isExpanded && (
                                <div className="pending-group-content">
                                    {group.pendingAnime.map(manga => renderMangaCard(manga, actions))}
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );

    // --- Detail Overlay Modal (same as anime) ---
    const renderDetailModal = () => {
        if (!detailManga) return null;
        return (
            <div className="anime-modal-overlay" onClick={() => setDetailManga(null)}>
                <div className="anime-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close-btn" onClick={() => setDetailManga(null)}>✕</button>

                    {detailManga.bannerImage && (
                        <img src={detailManga.bannerImage} alt="" className="anime-banner" />
                    )}

                    <div className="modal-content">
                        <div className="modal-header">
                            {detailManga.coverImage?.large && (
                                <img
                                    src={detailManga.coverImage.large}
                                    alt={getTitle(detailManga)}
                                    className="modal-cover"
                                />
                            )}
                            <div className="modal-header-info">
                                <h2>
                                    <a
                                        href={`https://anilist.co/manga/${detailManga.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {getTitle(detailManga)}
                                    </a>
                                </h2>
                                {detailManga.title?.english && detailManga.title.english !== detailManga.title?.romaji && (
                                    <p className="alt-title">{detailManga.title.english}</p>
                                )}
                                <p className="anime-meta">{formatMangaInfo(detailManga)}</p>
                                {detailManga.averageScore && (
                                    <div className="score-large">
                                        ⭐ {detailManga.averageScore / 10}/10
                                    </div>
                                )}
                                {detailManga.relationType && (
                                    <div className="relation-info" style={{ marginTop: '0.5rem' }}>
                                        <span className="relation-badge">
                                            {getRelationTypeDisplay(detailManga.relationType)}
                                        </span>
                                        {detailManga.relatedTo && (
                                            <span className="related-to">→ {detailManga.relatedTo.title}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {detailManga.description && (
                            <div className="modal-description">
                                <h3>Description</h3>
                                <div dangerouslySetInnerHTML={{ __html: detailManga.description }} />
                            </div>
                        )}

                        {detailManga.genres && detailManga.genres.length > 0 && (
                            <div className="modal-genres">
                                <h3>Genres</h3>
                                <div className="genre-list">
                                    {detailManga.genres.map((genre, idx) => (
                                        <span key={idx} className="genre-tag-large">{genre}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="modal-actions">
                            <a
                                href={`https://anilist.co/manga/${detailManga.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-anilist"
                            >
                                View on AniList
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Active view state
    const showGrouped = activeTab === 'new' ? newShowGrouped : removedShowGrouped;
    const setShowGrouped = activeTab === 'new' ? setNewShowGrouped : setRemovedShowGrouped;

    return (
        <div className="discover-tabs-container">
            {/* Tab Navigation */}
            <div className="discover-tabs-nav">
                <button
                    className={`tab-nav-btn ${activeTab === 'new' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('new'); setSelectedManga(new Set()); }}
                >
                    🔍 New Related Manga{newRelatedManga.length > 0 ? ` (${newRelatedManga.length})` : ''}
                </button>
                <button
                    className={`tab-nav-btn ${activeTab === 'removed' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('removed'); setSelectedManga(new Set()); }}
                >
                    🗑️ Removed Manga{removedMangaList.length > 0 ? ` (${removedMangaList.length})` : ''}
                </button>
            </div>

            {/* Tab Content */}
            <div className="discover-tabs-content">
                {activeTab === 'new' ? (
                    <div className="discover-container">
                        {/* Header */}
                        <div className="discover-header">
                            <div className="header-content">
                                <h1>🔍 Discover Related Manga</h1>
                                <p>Found {newRelatedManga.length} manga related to your list</p>
                            </div>
                            <button
                                onClick={() => setShowGrouped(!showGrouped)}
                                className="view-toggle-btn"
                                title={showGrouped ? 'Switch to Flat View' : 'Switch to Grouped View'}
                            >
                                {showGrouped ? '📋 Flat View' : '📚 Grouped View'}
                            </button>
                        </div>

                        {/* Success Message */}
                        {successMessage && <div className="success-banner">{successMessage}</div>}

                        {/* Batch Operation Progress UI */}
                        {batchOp.isOperationActive && (
                            <div className="batch-operation-progress" style={{
                                position: 'fixed',
                                bottom: '20px',
                                right: '20px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                padding: '15px 20px',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                zIndex: 1000,
                                minWidth: '300px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <strong>🔄 {batchOp.operationType === 'adding' ? 'Adding' : 'Updating'} Manga</strong>
                                    <button
                                        onClick={() => batchOp.cancelOperation()}
                                        style={{
                                            background: '#f44336',
                                            border: 'none',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                    Progress: {batchOp.completedItems}/{batchOp.totalItems} ({batchOp.getProgress()}%)
                                </div>
                                <div style={{
                                    width: '100%',
                                    height: '8px',
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        width: `${batchOp.getProgress()}%`,
                                        height: '100%',
                                        backgroundColor: '#4CAF50',
                                        transition: 'width 0.3s ease'
                                    }}></div>
                                </div>
                                {batchOp.currentItem && (
                                    <div style={{ marginTop: '8px', fontSize: '13px', opacity: 0.9 }}>
                                        Current: {batchOp.currentItem.title}
                                        {batchOp.currentItem.attempt > 1 && ` (Retry ${batchOp.currentItem.attempt})`}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Toolbar */}
                        {(pendingManga || []).length > 0 && (
                            <div className="discover-toolbar">
                                <div className="filter-group">
                                    <label>Filter by Relation:</label>
                                    <select value={filterRelationType} onChange={(e) => setFilterRelationType(e.target.value)}>
                                        {relationTypes.map(type => (
                                            <option key={type} value={type}>
                                                {type === 'ALL' ? 'All Types' : getRelationTypeDisplay(type)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Add as:</label>
                                    <select value={addStatus} onChange={(e) => setAddStatus(e.target.value)}>
                                        <option value="PLANNING">📅 Planning</option>
                                        <option value="CURRENT">▶️ Reading</option>
                                        <option value="PAUSED">⏸️ On Hold</option>
                                        <option value="COMPLETED">✅ Completed</option>
                                        <option value="DROPPED">❌ Dropped</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Quick Actions */}
                        {newRelatedManga.length > 0 && (
                            <div className="quick-actions">
                                <button onClick={selectedManga.size > 0 ? deselectAll : selectAll}>
                                    {selectedManga.size > 0 ? '❌ Deselect All' : `✅ Select All (${newRelatedManga.length})`}
                                </button>
                            </div>
                        )}

                        {/* Selection Actions */}
                        {selectedManga.size > 0 && (
                            <div className="selection-bar">
                                <span>{selectedManga.size} selected</span>
                                <div className="selection-actions">
                                    <button onClick={handleAddSelected} disabled={addingManga.size > 0} className="add-selected-btn">
                                        {addingManga.size > 0 ? '⏳ Adding...' : `➕ Add Selected (${selectedManga.size})`}
                                    </button>
                                    <button onClick={handleRemoveSelected} className="remove-selected-btn">
                                        🗑️ Remove Selected
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Content: Grouped or Flat */}
                        {showGrouped ? (
                            renderGroupedView(pendingGroups, 'add')
                        ) : (
                            <div className="pending-list">
                                {newRelatedManga.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon">🎉</div>
                                        <h3>All Caught Up!</h3>
                                        <p>No related manga found that you haven't added yet.</p>
                                        <p>Check back after adding more manga to your list.</p>
                                    </div>
                                ) : (
                                    newRelatedManga.map(manga => renderMangaCard(manga, 'add'))
                                )}
                            </div>
                        )}

                        {/* Summary Footer */}
                        <div className="discover-summary">
                            {showGrouped ? (
                                <p>
                                    Showing {pendingGroups.length} group{pendingGroups.length !== 1 ? 's' : ''} with{' '}
                                    {pendingGroups.reduce((sum, g) => sum + g.stats.count, 0)} of {(pendingManga || []).length} pending manga
                                </p>
                            ) : (
                                <p>Showing {newRelatedManga.length} of {(pendingManga || []).length} pending manga</p>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Removed Manga Tab */
                    <div className="discover-container">
                        <div className="discover-header">
                            <div className="header-content">
                                <h1>🗑️ Removed Manga</h1>
                                <p>{removedMangaList.length} manga hidden from suggestions</p>
                            </div>
                            <button
                                onClick={() => setShowGrouped(!showGrouped)}
                                className="view-toggle-btn"
                                title={showGrouped ? 'Switch to Flat View' : 'Switch to Grouped View'}
                            >
                                {showGrouped ? '📋 Flat View' : '📚 Grouped View'}
                            </button>
                        </div>

                        {/* Quick Actions */}
                        {removedMangaList.length > 0 && (
                            <div className="quick-actions">
                                <button onClick={selectedManga.size > 0 ? deselectAll : selectAll}>
                                    {selectedManga.size > 0 ? '❌ Deselect All' : `✅ Select All (${removedMangaList.length})`}
                                </button>
                            </div>
                        )}

                        {/* Selection Actions */}
                        {selectedManga.size > 0 && (
                            <div className="selection-bar">
                                <span>{selectedManga.size} selected</span>
                                <div className="selection-actions">
                                    <button onClick={handleRestoreSelected} className="add-selected-btn">
                                        ♻️ Restore Selected ({selectedManga.size})
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Content: Grouped or Flat */}
                        {showGrouped ? (
                            renderGroupedView(removedGroups, 'restore')
                        ) : (
                            <div className="pending-list">
                                {removedMangaList.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-icon">📭</div>
                                        <h3>No Removed Manga</h3>
                                        <p>You haven't hidden any related manga yet.</p>
                                    </div>
                                ) : (
                                    removedMangaList.map(manga => renderMangaCard(manga, 'restore'))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Detail Overlay Modal */}
            {renderDetailModal()}

            {/* Remove All Confirmation Modal */}
            <ConfirmationModal
                isOpen={showRemoveAllModal}
                onConfirm={confirmRemoveAllPending}
                onCancel={() => setShowRemoveAllModal(false)}
                title="Remove All Pending Manga?"
                message={`This will remove all ${newRelatedManga.length} manga from the current view. They will be moved to the Removed tab and can be restored later.\n\nContinue?`}
                confirmText="Remove All"
                cancelText="Cancel"
            />
        </div>
    );
};

export default MangaRelated;
